import type { Message } from '../types';
import { looksLikePlcCode } from './messageTopic';

export type DisplayMode =
  | 'simple'
  | 'snippet'
  | 'project'
  | 'debug'
  | 'safety_critical';

export type StageKind =
  | 'requirementThinking'
  | 'requirementAnalysis'
  | 'requirementConfirmation'
  | 'humanConfirmation'
  | 'engineeringReasoning'
  | 'planGeneration'
  | 'planReview'
  | 'code'
  | 'validation'
  | 'repair'
  /**
   * The native compiler's own row.
   *
   * The contract runs the checker's repair rounds first and the compiler's
   * after, with delivery behind both. Without a row the compiler's minutes
   * landed on whichever step was open — 2026-09-16 实测：「预检（内置规则）·
   * 6m50s」，检查器是毫秒级的；「修复问题 · 6m1s」，那一轮修复轮次是 0。
   */
  | 'codesysValidation';

export type StageVisibility = Record<StageKind, boolean>;

export interface DisplayStrategy {
  mode: DisplayMode;
  stages: StageVisibility;
}

// Main timeline order:
// Requirement analysis -> requirement confirmation -> plan generation ->
// plan approval -> code generation -> validation -> repair
//
// Notes:
// - requirementThinking is rendered as a lightweight progress/status panel, not a
//   formal right-side timeline node.
// - engineeringReasoning remains an internal causal stage presented inside the
//   plan-generation panel. planReview is visible so approval source is auditable.
export const STAGE_ORDER: StageKind[] = [
  'requirementAnalysis',
  'requirementConfirmation',
  'planGeneration',
  'planReview',
  'code',
  'validation',
  'repair',
  'codesysValidation',
];

const ALL_STAGES_VISIBLE: StageVisibility = {
  requirementThinking: true,
  requirementAnalysis: true,
  requirementConfirmation: true,
  humanConfirmation: true,
  engineeringReasoning: true,
  planGeneration: true,
  planReview: true,
  code: true,
  validation: true,
  repair: true,
  codesysValidation: true,
};

const NO_STAGES_VISIBLE: StageVisibility = {
  requirementThinking: false,
  requirementAnalysis: false,
  requirementConfirmation: false,
  humanConfirmation: false,
  engineeringReasoning: false,
  planGeneration: false,
  planReview: false,
  code: false,
  validation: false,
  repair: false,
  codesysValidation: false,
};

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function safeStringify(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value).slice(0, 6000);
  } catch {
    return '';
  }
}

function getPlanProcessSummaries(message: Message): Array<Record<string, unknown>> {
  const summaries: Array<Record<string, unknown>> = [];
  if (message.planProcessSummary && typeof message.planProcessSummary === 'object') {
    summaries.push(message.planProcessSummary as unknown as Record<string, unknown>);
  }
  for (const item of message.planProcessHistory || []) {
    if (item?.summary && typeof item.summary === 'object') {
      summaries.push(item.summary as unknown as Record<string, unknown>);
    }
  }
  return summaries;
}

function hasPlanSummaryOutputType(message: Message, outputType: string): boolean {
  return getPlanProcessSummaries(message).some(summary => String(summary.outputType || '').toLowerCase() === outputType);
}

function latestPlanReview(message: Message): Message['planReview'] {
  const history = message.planProcessHistory || [];
  for (let index = history.length - 1; index >= 0; index -= 1) {
    if (history[index]?.planReview) return history[index].planReview;
  }
  return message.planReview || message.stageBuffers?.planReview?.artifact as Message['planReview'];
}

function hasCodeArtifact(message: Message): boolean {
  const code = message.parsed?.code;
  return Boolean(hasText(code) && looksLikePlcCode(code));
}

function hasValidationOrRepairArtifact(message: Message): boolean {
  return Boolean(
    message.parsed?.verification
      || message.pipeline?.validationReport
      || message.stageBuffers?.validation?.artifact
      || message.stageBuffers?.validation?.started
      || message.stageBuffers?.repair?.artifact
      || message.stageBuffers?.repair?.started
      || message.repairAppendix?.finalCode
      || message.repairAppendix?.finalValidation
      || (message.repairAppendix && message.repairAppendix.status && message.repairAppendix.status !== 'not_started'),
  );
}

function hasPlanReviewArtifact(message: Message): boolean {
  const planReview = latestPlanReview(message);
  if (!planReview) {
    return Boolean(
      message.stageBuffers?.planReview?.artifact
        || message.planProcessHistory?.some(item => item?.planReview),
    );
  }
  return true;
}

function hasAttentionRequiredStage(message: Message): boolean {
  const planReview = latestPlanReview(message);
  const validationFailed = Boolean(
    message.pipeline?.validationReport
      && (message.pipeline.validationReport as any).valid === false,
  ) || message.stageBuffers?.validation?.status === 'failed';
  const repairStatus = String(message.repairAppendix?.status || message.stageBuffers?.repair?.status || '').toLowerCase();

  return Boolean(
    (
        planReview
          && planReview.requiresApproval !== false
          && !planReview.reviewResult
      )
      || planReview?.reviewResult === 'rejected'
      || planReview?.reviewResult === 'regenerate_subtask'
      || validationFailed
      || repairStatus === 'failed',
  );
}

function hasActiveRequirementPipelineProgress(message: Message): boolean {
  const phase = String(message.progressState?.phase || '').toLowerCase();
  const stage = String(message.progressState?.stage || '').toLowerCase();
  const planPhase = String(message.progressState?.planPhase || '').toLowerCase();
  return Boolean(
    message.isStreaming
      && (
        phase === 'classifying'
          || phase === 'clarifying'
          || stage === 'routing'
          || stage === 'requirement_analysis'
          || stage === 'clarification'
          || (phase === 'planning' && (!planPhase || planPhase === 'analyzing' || planPhase === 'classifying'))
      ),
  );
}

function hasStructuredCodePipeline(message: Message): boolean {
  const pipeline = message.pipeline as Record<string, any> | undefined;
  const requirement = pipeline?.requirement as Record<string, any> | undefined;
  return Boolean(
    pipeline?.code
      || pipeline?.vendorRules
      || pipeline?.testCases?.length
      || pipeline?.validationReport
      || requirement?.taskType
      || requirement?.controlStrategy
      || requirement?.brand
      || requirement?.language
      || requirement?.ioPoints?.length
      || requirement?.ioSignals?.length
      || requirement?.keyConditions?.length
      || requirement?.safetyConstraints?.length,
  );
}

function collectIntentText(message: Message): string {
  const requirement = message.pipeline?.requirement as unknown as Record<string, unknown> | undefined;
  const planReview = message.planReview as unknown as Record<string, unknown> | undefined;
  const historyText = (message.planProcessHistory || [])
    .map(item => [item?.stage, item?.reasoning, safeStringify(item?.summary), safeStringify(item?.planReview)].filter(Boolean).join(' '))
    .join('\n');

  return [
    message.content,
    message.reasoning,
    message.planReasoning,
    message.planThinkingReasoning,
    message.planAnalysisReasoning,
    message.planGenerationReasoning,
    message.stageBuffers?.thinking?.text,
    message.stageBuffers?.requirementAnalysis?.text,
    message.stageBuffers?.planBasis?.text,
    message.stageBuffers?.planGeneration?.text,
    message.stageBuffers?.validation?.text,
    message.stageBuffers?.repair?.text,
    message.planTransitionText,
    message.parsed?.summary,
    message.parsed?.explanation,
    message.parsed?.clarify,
    requirement?.rawPrompt,
    requirement?.normalizedPrompt,
    requirement?.taskType,
    requirement?.controlStrategy,
    safeStringify(requirement?.desiredOutputs),
    safeStringify(requirement?.entities),
    planReview?.requirementSummary,
    safeStringify(message.planProcessSummary),
    safeStringify(message.stageBuffers?.planReview?.artifact),
    safeStringify(message.stageBuffers?.validation?.artifact),
    safeStringify(message.stageBuffers?.repair?.artifact),
    historyText,
    safeStringify(message.clarificationChecklist),
  ].filter(Boolean).join('\n').slice(0, 12000);
}

function looksLikeCodeGenerationIntent(text: string): boolean {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (!normalized) return false;

  // Explicit requests to generate PLC code / program / function block.
  const explicitCodeGeneration = /(?:生成|编写|写(?:一份|一个|段)?|实现|开发|创建|输出|给出|导出|做(?:一个|一份)?|设计).{0,60}(?:PLC|代码|程序|逻辑|ST|SCL|Structured\s*Text|功能块|函数块|FB|FC|OB|梯形图|LAD|FBD|CODESYS|博途|TIA|IEC\s*61131)|(?:generate|write|create|implement|build|develop|produce|export|design).{0,80}(?:PLC|code|program|logic|ST|SCL|Structured\s*Text|function\s*block|FB|FC|OB|ladder|LAD|FBD|CODESYS|TIA|IEC\s*61131)/i;
  if (explicitCodeGeneration.test(normalized)) return true;

  // Typical automation-control prompts omit the word "code" but still mean a PLC
  // generation workflow, e.g. "Control a conveyor belt..." / "控制输送线启停...".
  const industrialControlTask = /(?:控制|自动化|实现|设计|联锁|顺控|启停|急停|报警|定时|计时|超时|互锁|PID|电机|水泵|泵|阀|输送线|传送带|传感器|液位|温度|交通灯|批处理|包装线).{0,80}(?:控制|逻辑|PLC|程序|代码|ST|SCL|功能块|顺控|联锁|电机|泵|阀|输送线|传感器|PID|定时|报警|急停)|(?:control|automate|sequence|interlock|start\/?stop|e-?stop|alarm|timer|timeout|PID|motor|pump|valve|conveyor|sensor|tank|traffic\s*light|batch|packaging).{0,80}(?:PLC|logic|program|code|function\s*block|ST|SCL|control|sequence|interlock|timer|alarm|sensor|motor|pump|valve)/i;
  if (industrialControlTask.test(normalized)) return true;

  // Structured pipeline metadata commonly uses these task names.
  return /(?:code_generation|generate_code|plc_program|control_logic|logic_generation|program_generation|structured_text_generation)/i.test(normalized);
}

function isExplicitNonCodeOutput(message: Message): boolean {
  if (hasCodeArtifact(message) || hasValidationOrRepairArtifact(message) || hasPlanReviewArtifact(message)) return false;
  if (hasPlanSummaryOutputType(message, 'code')) return false;

  // Backend explicitly routed this turn as explanation/chat. In that case the
  // right-side execution timeline should stay hidden even if the text mentions PLC.
  if (hasPlanSummaryOutputType(message, 'explain')) return true;

  const text = collectIntentText(message);
  return /(?:解释|说明|分析|总结|润色|翻译|比较|推荐|介绍|是什么|为什么|怎么学习|review|explain|summari[sz]e|translate|compare|recommend|introduction|what\s+is|why\s+is)/i.test(text)
    && !looksLikeCodeGenerationIntent(text);
}

/**
 * Whether this assistant message belongs to the PLC code-generation workflow.
 *
 * This deliberately filters out general chat / explanation / document analysis
 * messages that may still carry progressState or lightweight pipeline metadata.
 * The timeline is only meaningful for real code-generation turns.
 */
export function isCodePipelineMessage(message: Message): boolean {
  if (message.offTopic) return false;

  if (hasCodeArtifact(message) || hasValidationOrRepairArtifact(message)) return true;
  if (hasPlanSummaryOutputType(message, 'code')) return true;
  if (hasPlanReviewArtifact(message)) return true;
  if (hasStructuredCodePipeline(message)) return true;
  if (hasActiveRequirementPipelineProgress(message)) return true;
  if (isExplicitNonCodeOutput(message)) return false;

  const text = collectIntentText(message);
  return looksLikeCodeGenerationIntent(text);
}

function hasProjectPipelineSignal(message: Message): boolean {
  return Boolean(
    message.progressState
      || message.pipeline
      || message.planThinkingReasoning
      || message.planAnalysisReasoning
      || message.planGenerationReasoning
      || message.planTransitionText
      || message.stageBuffers
      || message.planProcessSummary
      || message.planProcessHistory?.length
      || message.planReview
      || message.clarificationChecklist?.length
      || message.pendingChipIds?.length
      || message.parsed?.clarify
      || message.parsed?.verification
      || message.repairAppendix,
  );
}

export function deriveDisplayMode(message: Message): DisplayStrategy {
  // 1. Safety-critical snippets keep the narrow safety view even if repair
  // metadata is also present.
  if (message.strictSafetyCheck && message.parsed?.code) {
    return {
      mode: 'safety_critical',
      stages: {
        ...NO_STAGES_VISIBLE,
        code: true,
        validation: true,
        repair: true,
  codesysValidation: true,
      },
    };
  }

  // 2. Debug / repair: only validation + repair when this is a standalone repair view.
  const isRepairing =
    message.repairAppendix?.status === 'repairing'
    || message.progressState?.phase === 'repairing';
  const hasRicherProjectContext = Boolean(
    message.pipeline
      || message.stageBuffers
      || message.planProcessSummary
      || message.planProcessHistory?.length
      || message.planReview
      || message.parsed?.code,
  );
  if (isRepairing && !hasRicherProjectContext) {
    return {
      mode: 'debug',
      stages: {
        ...NO_STAGES_VISIBLE,
        validation: true,
        repair: true,
  codesysValidation: true,
      },
    };
  }

  // 3. Full project pipeline only for real PLC code-generation tasks.
  // General Q&A / document analysis can still stream progress, but should not show
  // the execution timeline or the code-generation pipeline sections.
  if (!message.offTopic && hasProjectPipelineSignal(message) && isCodePipelineMessage(message)) {
    return {
      mode: 'project',
      stages: { ...ALL_STAGES_VISIBLE },
    };
  }

  // 4. Snippet (code without pipeline)
  if (message.parsed?.code && looksLikePlcCode(message.parsed.code)) {
    return {
      mode: 'snippet',
      stages: {
        ...NO_STAGES_VISIBLE,
        code: true,
        validation: true, // badge only in snippet mode
      },
    };
  }

  // 5. Simple (everything else)
  return {
    mode: 'simple',
    stages: { ...NO_STAGES_VISIBLE },
  };
}

/**
 * Whether this message has engineering material worth a workspace dashboard.
 *
 * This no longer opens anything on the user's behalf — opening the workspace is
 * always an explicit action. It only decides which view that action lands on:
 * a dashboard when there is a live pipeline to watch, the full view otherwise.
 */
export function hasEngineeringDashboardContent(message: Message): boolean {
  if (message.role !== 'assistant' || message.offTopic) return false;

  const displayMode = deriveDisplayMode(message).mode;
  if (displayMode !== 'project' && displayMode !== 'debug' && displayMode !== 'safety_critical') {
    return false;
  }

  if (displayMode === 'debug' || displayMode === 'safety_critical') {
    return true;
  }

  if (hasAttentionRequiredStage(message)) return true;

  // A code-generation intent or a started stage is not yet an artifact. Keep the
  // conversation as the main surface until something editable actually exists.
  return Boolean(hasCodeArtifact(message) || hasText(message.repairAppendix?.finalCode));
}

export type StageStatus =
  | 'pending'
  | 'running'
  | 'needs_input'
  | 'needs_approval'
  | 'passed'
  | 'failed'
  /**
   * The step did its work but not all of it, and nothing is waiting on the user.
   *
   * The CODESYS handoff is the case this exists for: the background compile
   * passed and the project was written to disk, but the desktop IDE did not
   * open. A tick would claim an IDE the user cannot see, and a cross would
   * throw away a compile that genuinely passed.
   */
  | 'warning'
  | 'repaired'
  | 'skipped';

export function shouldStageBeExpanded(
  status: StageStatus,
  isSafetyRelated: boolean,
): boolean {
  // Always expanded: running, failed, warning, needs_input, needs_approval
  if (status === 'running' || status === 'failed' || status === 'warning' ||
      status === 'needs_input' || status === 'needs_approval') {
    return true;
  }
  // Safety-related always visible
  if (isSafetyRelated) return true;
  // Passed/skipped → collapsed
  return false;
}
